/**
 * 「基于当前查询发起任务」确认弹窗。
 *
 * 出现时机:用户在 JobTaskList 页面完成一轮筛选(filteredCount 非空),点
 * FilteredStatRow 右侧的「基于当前查询发起任务」按钮 —— 先弹这个 confirm 核对
 * 「按什么发」,再打开 CreateJobModal 走完整创建流程。
 *
 * 设计基调:精工小仪表盘 —— 灰阶主色 + 单一强调色。
 *   顶部「场景池来源」块:jobId + jobName + 场景数/任务数,用等宽字体强调数字
 *   中部「已应用的筛选」列表:每条 filter 中文名 + 值,filter 名与值用左右对齐
 *   底部两键:取消 / 下一步(primary)
 */
import { Modal, Button } from 'antd';
import type { FilterEntry } from './filterLabels';

interface Props {
  open: boolean;
  onCancel: () => void;
  /** 点「下一步」后触发,由父层关本弹窗并打开 CreateJobModal。 */
  onConfirm: () => void;
  jobId: number;
  jobName?: string;
  sceneCount: number;
  taskCount: number;
  /** 当前应用的 filter(已按 FILTER_KEY_ORDER 顺序、已过滤空值)。 */
  filters: FilterEntry[];
  /** 弹窗标题;默认「基于当前筛选项创建任务」,勾选任务入口传自定义标题。 */
  title?: string;
}

const MONO =
  '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Consolas, monospace';

export default function RerunFromQueryConfirmModal({
  open,
  onCancel,
  onConfirm,
  jobId,
  jobName,
  sceneCount,
  taskCount,
  filters,
  title = '基于当前筛选项创建任务',
}: Props) {
  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      width={560}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onCancel}>取消</Button>
          <Button
            type="primary"
            onClick={onConfirm}
            disabled={sceneCount === 0}
          >
            下一步:配置任务
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* 场景池来源块 */}
        <section>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#8c8c8c',
              letterSpacing: 0.4,
              marginBottom: 6,
            }}
          >
            场景池来源
          </div>
          <div
            style={{
              padding: '10px 12px',
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 13,
            }}
          >
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
              <span style={{ color: '#8c8c8c' }}>源 Job</span>
              <span style={{ fontFamily: MONO, color: '#1f1f1f' }}>#{jobId}</span>
              {jobName ? (
                <span style={{ color: '#595959', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {jobName}
                </span>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'baseline' }}>
              <span>
                <span style={{ color: '#8c8c8c', marginRight: 4 }}>场景数</span>
                <strong
                  style={{
                    color: sceneCount === 0 ? '#bfbfbf' : '#1677ff',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 15,
                  }}
                >
                  {sceneCount}
                </strong>
              </span>
              <span>
                <span style={{ color: '#8c8c8c', marginRight: 4 }}>任务数</span>
                <strong
                  style={{
                    color: '#595959',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 15,
                  }}
                >
                  {taskCount}
                </strong>
              </span>
            </div>
          </div>
        </section>

        {/* 已应用筛选块 */}
        <section>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#8c8c8c',
              letterSpacing: 0.4,
              marginBottom: 6,
            }}
          >
            已应用的筛选(共 {filters.length} 项)
          </div>
          {filters.length === 0 ? (
            <div
              style={{
                padding: '12px',
                border: '1px dashed #d9d9d9',
                borderRadius: 6,
                color: '#bfbfbf',
                fontSize: 13,
              }}
            >
              未应用任何筛选 —— 会用该 Job 的全部场景发起任务
            </div>
          ) : (
            <ul
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              {filters.map((f, idx) => (
                <li
                  key={f.key}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 12,
                    padding: '8px 12px',
                    fontSize: 13,
                    borderTop: idx === 0 ? undefined : '1px solid #f5f5f5',
                    background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                  }}
                >
                  <span
                    style={{
                      color: '#8c8c8c',
                      minWidth: 96,
                      flexShrink: 0,
                    }}
                  >
                    {f.label}
                  </span>
                  <span
                    style={{
                      color: '#1f1f1f',
                      flex: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {f.valueLabel}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
          确认后进入创建任务弹窗,场景池已锁定,只需配置版本/参数即可提交。
        </div>
      </div>
    </Modal>
  );
}
